/**
 * Lista fija de países (ISO 3166-1) para los campos "País de origen".
 *
 * Se resolvió con una lista en código y no con un catálogo administrable
 * porque los países no cambian y no requiere mantenimiento por parte del
 * usuario. El valor guardado es el NOMBRE en español y en MAYÚSCULAS, para
 * ser compatible con los registros históricos que ya se capturaron como
 * texto libre (p. ej. `equipments.manufacture_country`).
 *
 * `id === name` a propósito: así un registro viejo con "ALEMANIA" sigue
 * preseleccionándose correctamente en el desplegable.
 */
export interface CountryOption {
  id:    string;   // nombre en MAYÚSCULAS (lo que se persiste)
  name:  string;   // etiqueta visible
  code:  string;   // ISO 3166-1 alpha-2 (referencia / futuros reportes)
}

const RAW: [string, string][] = [
  ['AF', 'Afganistán'], ['ZA', 'Sudáfrica'], ['AL', 'Albania'], ['DE', 'Alemania'],
  ['AD', 'Andorra'], ['AO', 'Angola'], ['SA', 'Arabia Saudita'], ['DZ', 'Argelia'],
  ['AR', 'Argentina'], ['AM', 'Armenia'], ['AU', 'Australia'], ['AT', 'Austria'],
  ['AZ', 'Azerbaiyán'], ['BS', 'Bahamas'], ['BD', 'Bangladés'], ['BB', 'Barbados'],
  ['BH', 'Baréin'], ['BE', 'Bélgica'], ['BZ', 'Belice'], ['BJ', 'Benín'],
  ['BY', 'Bielorrusia'], ['BO', 'Bolivia'], ['BA', 'Bosnia y Herzegovina'],
  ['BW', 'Botsuana'], ['BR', 'Brasil'], ['BN', 'Brunéi'], ['BG', 'Bulgaria'],
  ['BF', 'Burkina Faso'], ['BI', 'Burundi'], ['BT', 'Bután'], ['CV', 'Cabo Verde'],
  ['KH', 'Camboya'], ['CM', 'Camerún'], ['CA', 'Canadá'], ['QA', 'Catar'],
  ['TD', 'Chad'], ['CL', 'Chile'], ['CN', 'China'], ['CY', 'Chipre'],
  ['VA', 'Ciudad del Vaticano'], ['CO', 'Colombia'], ['KM', 'Comoras'],
  ['KP', 'Corea del Norte'], ['KR', 'Corea del Sur'], ['CI', 'Costa de Marfil'],
  ['CR', 'Costa Rica'], ['HR', 'Croacia'], ['CU', 'Cuba'], ['DK', 'Dinamarca'],
  ['DM', 'Dominica'], ['EC', 'Ecuador'], ['EG', 'Egipto'], ['SV', 'El Salvador'],
  ['AE', 'Emiratos Árabes Unidos'], ['ER', 'Eritrea'], ['SK', 'Eslovaquia'],
  ['SI', 'Eslovenia'], ['ES', 'España'], ['US', 'Estados Unidos'], ['EE', 'Estonia'],
  ['ET', 'Etiopía'], ['PH', 'Filipinas'], ['FI', 'Finlandia'], ['FJ', 'Fiyi'],
  ['FR', 'Francia'], ['GA', 'Gabón'], ['GM', 'Gambia'], ['GE', 'Georgia'],
  ['GH', 'Ghana'], ['GD', 'Granada'], ['GR', 'Grecia'], ['GT', 'Guatemala'],
  ['GN', 'Guinea'], ['GQ', 'Guinea Ecuatorial'], ['GW', 'Guinea-Bisáu'],
  ['GY', 'Guyana'], ['HT', 'Haití'], ['HN', 'Honduras'], ['HU', 'Hungría'],
  ['IN', 'India'], ['ID', 'Indonesia'], ['IQ', 'Irak'], ['IR', 'Irán'],
  ['IE', 'Irlanda'], ['IS', 'Islandia'], ['IL', 'Israel'], ['IT', 'Italia'],
  ['JM', 'Jamaica'], ['JP', 'Japón'], ['JO', 'Jordania'], ['KZ', 'Kazajistán'],
  ['KE', 'Kenia'], ['KG', 'Kirguistán'], ['KI', 'Kiribati'], ['KW', 'Kuwait'],
  ['LA', 'Laos'], ['LS', 'Lesoto'], ['LV', 'Letonia'], ['LB', 'Líbano'],
  ['LR', 'Liberia'], ['LY', 'Libia'], ['LI', 'Liechtenstein'], ['LT', 'Lituania'],
  ['LU', 'Luxemburgo'], ['MK', 'Macedonia del Norte'], ['MG', 'Madagascar'],
  ['MY', 'Malasia'], ['MW', 'Malaui'], ['MV', 'Maldivas'], ['ML', 'Malí'],
  ['MT', 'Malta'], ['MA', 'Marruecos'], ['MU', 'Mauricio'], ['MR', 'Mauritania'],
  ['MX', 'México'], ['FM', 'Micronesia'], ['MD', 'Moldavia'], ['MC', 'Mónaco'],
  ['MN', 'Mongolia'], ['ME', 'Montenegro'], ['MZ', 'Mozambique'], ['MM', 'Myanmar'],
  ['NA', 'Namibia'], ['NR', 'Nauru'], ['NP', 'Nepal'], ['NI', 'Nicaragua'],
  ['NE', 'Níger'], ['NG', 'Nigeria'], ['NO', 'Noruega'], ['NZ', 'Nueva Zelanda'],
  ['OM', 'Omán'], ['NL', 'Países Bajos'], ['PK', 'Pakistán'], ['PW', 'Palaos'],
  ['PS', 'Palestina'], ['PA', 'Panamá'], ['PG', 'Papúa Nueva Guinea'],
  ['PY', 'Paraguay'], ['PE', 'Perú'], ['PL', 'Polonia'], ['PT', 'Portugal'],
  ['GB', 'Reino Unido'], ['CF', 'República Centroafricana'], ['CZ', 'República Checa'],
  ['CG', 'República del Congo'], ['CD', 'República Democrática del Congo'],
  ['DO', 'República Dominicana'], ['RW', 'Ruanda'], ['RO', 'Rumania'], ['RU', 'Rusia'],
  ['WS', 'Samoa'], ['KN', 'San Cristóbal y Nieves'], ['SM', 'San Marino'],
  ['VC', 'San Vicente y las Granadinas'], ['LC', 'Santa Lucía'],
  ['ST', 'Santo Tomé y Príncipe'], ['SN', 'Senegal'], ['RS', 'Serbia'],
  ['SC', 'Seychelles'], ['SL', 'Sierra Leona'], ['SG', 'Singapur'], ['SY', 'Siria'],
  ['SO', 'Somalia'], ['LK', 'Sri Lanka'], ['SZ', 'Suazilandia'], ['SD', 'Sudán'],
  ['SS', 'Sudán del Sur'], ['SE', 'Suecia'], ['CH', 'Suiza'], ['SR', 'Surinam'],
  ['TH', 'Tailandia'], ['TZ', 'Tanzania'], ['TJ', 'Tayikistán'], ['TL', 'Timor Oriental'],
  ['TG', 'Togo'], ['TO', 'Tonga'], ['TT', 'Trinidad y Tobago'], ['TN', 'Túnez'],
  ['TM', 'Turkmenistán'], ['TR', 'Turquía'], ['TV', 'Tuvalu'], ['UA', 'Ucrania'],
  ['UG', 'Uganda'], ['UY', 'Uruguay'], ['UZ', 'Uzbekistán'], ['VU', 'Vanuatu'],
  ['VE', 'Venezuela'], ['VN', 'Vietnam'], ['YE', 'Yemen'], ['DJ', 'Yibuti'],
  ['ZM', 'Zambia'], ['ZW', 'Zimbabue'],
];

/** Opciones listas para un `select` de los diálogos genéricos. */
export const COUNTRY_OPTIONS: CountryOption[] = RAW
  .map(([code, name]) => ({ id: name.toUpperCase(), name, code }))
  .sort((a, b) => a.name.localeCompare(b.name, 'es'));

/** Normaliza un valor histórico de texto libre al id del catálogo. */
export function normalizeCountry(value: any): string | null {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const raw = String(value).trim().toUpperCase();
  const byId = COUNTRY_OPTIONS.find(c => c.id === raw);
  if (byId) return byId.id;
  const byCode = COUNTRY_OPTIONS.find(c => c.code === raw);
  return byCode ? byCode.id : raw;
}
